import React, { FC, useState } from 'react'

import { createCookie, getCookie } from '@smarty-nx/code-utils'
import { getDismissibleCardById } from '@smarty-nx/contentful'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'

import { content } from './customer-raf-modal.content'
import classes from './customer-raf-modal.module.scss'
import {
  Modal,
  Typography,
  TextInput,
  Button,
  Level,
  ContentfulProcessor,
} from '../../../index'

interface CustomerRafModalProps {
  userId: string | undefined
  buyapowaShareUrl: string
  referFriendUrl: string
  dataItemCategoryCopyLink?: string
  dataItemCategoryReferNow?: string
  dataItemNameCopyLink?: string
  dataItemNameReferNow?: string
}

const RAF_MODAL_ID = 'earn_rewards_dashboard_card'
const rafModalData = getDismissibleCardById(RAF_MODAL_ID)

export const CustomerRafModal: FC<CustomerRafModalProps> = ({
  userId,
  buyapowaShareUrl,
  referFriendUrl,
  dataItemCategoryCopyLink,
  dataItemCategoryReferNow,
  dataItemNameCopyLink,
  dataItemNameReferNow,
}) => {
  const history = useHistory()

  const [showModal, setShowModal] = useState(true)

  // Modal should appear no more than 1 time per session and no more than once in 30 days
  const shouldShowModal = () => {
    const rafModalDate = getCookie({ name: `${userId}-rafModalDate` })

    // Modal already shown in current session
    if (window.localStorage.getItem('rafModalShown') === 'true') {
      return false
    }

    // We show modal for the first time
    if (!rafModalDate) {
      return true
    }

    return dayjs(dayjs()).diff(rafModalDate, 'days') > 30
  }

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(buyapowaShareUrl).catch((error) => {
      console.error(error)
    })
    e.currentTarget.blur()
  }

  const handleClose = () => {
    setShowModal(false)
    window.localStorage.setItem('rafModalShown', 'true')

    createCookie({
      name: `${userId}-rafModalDate`,
      value: dayjs().toString(),
      days: 30,
    })
    setShowModal(false)
    // Fallback cleanup if the react-modal does not remove up the class from the body.
    // https://github.com/reactjs/react-modal/issues/888
    document.body.classList.remove('ReactModal__BodyRaf')
  }

  const handleNavigate = () => {
    handleClose()
    history.push(referFriendUrl)
  }

  if (!showModal || !shouldShowModal()) {
    return null
  }

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={handleClose}
      testId="customer-raf-modal"
      modalClassName={classes.modalContainer}
      closeButtonClassName={classes.closeButton}
      closeModalButtonItemName={'dismiss-raf'}
      bodyOpenClassName="ReactModal__BodyRaf"
    >
      <div className={classes.root}>
        <div className={classes.modalHeader}>
          <img
            className={classes.rafBanner}
            src={rafModalData?.backgroundImage?.file?.url}
            alt=""
          />
        </div>
        <div className={classes.modalBody}>
          <Typography
            variant="Kollektif/Large"
            component="h2"
            dataTestId="customer-raf-title"
          >
            {rafModalData?.title || ''}
          </Typography>
          <div data-testid="customer-raf-description">
            <ContentfulProcessor
              content={rafModalData?.markdownDescription || ''}
            />
          </div>
          <div className={classes.copyUrlWrapper}>
            <TextInput
              labelContent={''}
              onReset={() => null}
              type="text"
              value={buyapowaShareUrl}
              descriptionDataTestId="customer-raf-description"
              descriptionClassName={classes.noDescription}
              wrapperClassName={classes.inputWrapper}
              readOnly
              showResetButton={false}
              dataTestId="customer-raf-input"
            />
            <div className={classes.desktop}>
              <Button
                className={classes.copyButton}
                onClick={handleCopy}
                data-testid="customer-raf-copy-button"
                level={Level.Quaternary}
                {...(dataItemCategoryCopyLink && {
                  'data-item-category': dataItemCategoryCopyLink,
                })}
                {...(dataItemNameCopyLink && {
                  'data-item-name': dataItemNameCopyLink,
                })}
              >
                {content.copyCta}
              </Button>
            </div>
          </div>
          <Typography
            variant="Kollektif/Tiny"
            dataTestId="customer-raf-info-text"
          >
            {content.infoText}
          </Typography>
          <div className={classes.mobile}>
            <Button
              className={classes.copyButton}
              onClick={handleCopy}
              data-testid="customer-raf-copy-button"
              level={Level.Quaternary}
              {...(dataItemCategoryCopyLink && {
                'data-item-category': dataItemCategoryCopyLink,
              })}
              {...(dataItemNameCopyLink && {
                'data-item-name': dataItemNameCopyLink,
              })}
            >
              {content.copyCta}
            </Button>
          </div>
          <div className={classes.referNowButton}>
            <Button
              dataTestId="refer-now-button"
              {...(dataItemCategoryReferNow && {
                'data-item-category': dataItemCategoryReferNow,
              })}
              {...(dataItemNameReferNow && {
                'data-item-name': dataItemNameReferNow,
              })}
              size="regular"
              onClick={handleNavigate}
            >
              {rafModalData?.ctaLink || ''}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
